import { useMemo } from 'react';
import { useAdminContracts } from './useAdminContracts';
import { useAdminRoomTypes } from './useAdminRoomTypes';
import { useAdminResidences } from './useAdminResidences';
import { useAdminBookings } from './useAdminBookings';
import type { RoomType, Residence, Contract, Booking } from '@/shared/types';
import type {
  OccupancyBar,
  RoomRow,
  StudentInfo,
  AvailabilityFilters,
} from './useAdminAvailabilityCalendar';

/** Width of a single month column in the year grid, in pixels. */
export const MONTH_WIDTH_PX = 96;

export interface MonthBarGeometry {
  leftPx: string;
  widthPx: string;
  isPartialStart: boolean;
  isPartialEnd: boolean;
}

/**
 * Position a [start, endExclusive) range in a 12-month-column grid for `year`.
 * A range covers a month column if it overlaps that month. The result is clamped
 * to the year (Jan..Dec); isPartialStart/isPartialEnd flag when the true range
 * extends before/after the displayed year. Returns null when the range does not
 * intersect the year at all.
 */
export function monthBarGeometry(
  start: Date,
  endExclusive: Date,
  year: number
): MonthBarGeometry | null {
  const yearStart = new Date(year, 0, 1);
  const yearEndExclusive = new Date(year + 1, 0, 1);
  if (start >= yearEndExclusive || endExclusive <= yearStart) return null;

  const monthsFrom = (d: Date) => (d.getFullYear() - year) * 12 + d.getMonth();
  const rawStart = monthsFrom(start);
  // Exclusive end rounds up to the next column only when it lands past the
  // first of the month (i.e. the range still touches that month).
  const rawEndExclusive =
    monthsFrom(new Date(endExclusive.getFullYear(), endExclusive.getMonth(), 1)) +
    (endExclusive.getDate() > 1 ? 1 : 0);

  const clampedStart = Math.max(0, rawStart);
  const clampedEnd = Math.min(12, rawEndExclusive);

  return {
    leftPx: `${clampedStart * MONTH_WIDTH_PX}px`,
    widthPx: `${(clampedEnd - clampedStart) * MONTH_WIDTH_PX}px`,
    isPartialStart: rawStart < 0,
    isPartialEnd: rawEndExclusive > 12,
  };
}

/**
 * Merge overlapping same-row contract bars (year geometry). Mirrors the
 * month-view merge in useAdminAvailabilityCalendar but recomputes width in
 * month columns and keeps the combined student list.
 */
function mergeOverlappingBars(bars: OccupancyBar[], year: number): OccupancyBar[] {
  const sorted = [...bars].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  const result: OccupancyBar[] = [];
  for (const bar of sorted) {
    const last = result[result.length - 1];
    if (last && bar.startDate <= last.endDate) {
      last.studentName = `${last.studentName} / ${bar.studentName}`;
      last.students = [...(last.students ?? []), ...(bar.students ?? [])];
      const newEnd = new Date(Math.max(last.endDate.getTime(), bar.endDate.getTime()));
      last.endDate = newEnd;
      // Recompute the merged bar geometry from its true (unclamped) span.
      const geo = monthBarGeometry(last.startDate, addDaysExclusive(newEnd), year);
      if (geo) {
        last.leftPx = geo.leftPx;
        last.widthPx = geo.widthPx;
        last.isPartialStart = geo.isPartialStart;
        last.isPartialEnd = geo.isPartialEnd;
      }
    } else {
      result.push({ ...bar, students: [...(bar.students ?? [])] });
    }
  }
  return result;
}

/** endDate is inclusive; the exclusive end is the day after. */
function addDaysExclusive(inclusiveEnd: Date): Date {
  const d = new Date(inclusiveEnd);
  d.setDate(d.getDate() + 1);
  return d;
}

/**
 * Day-precision x-offset within the 12-month year grid (px). Used for the
 * "actual stay" overlay inside month-granular bars. `endOfDay` positions the
 * end of the given day instead of its start. Clamped to the year edges.
 */
function dayOffsetPx(date: Date, year: number, endOfDay = false): number {
  if (date.getFullYear() < year) return 0;
  if (date.getFullYear() > year) return 12 * MONTH_WIDTH_PX;
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const frac = (date.getDate() - (endOfDay ? 0 : 1)) / daysInMonth;
  return (date.getMonth() + frac) * MONTH_WIDTH_PX;
}

function buildRoomRows(
  roomTypes: RoomType[],
  contracts: Contract[],
  calendarBookings: Booking[],
  residenceMap: Map<string, { name: string; address: string }>,
  year: number,
  filters: AvailabilityFilters
): RoomRow[] {
  const filteredRoomTypes =
    filters.residenceId === 'all'
      ? roomTypes
      : roomTypes.filter(rt => rt.residenceId === filters.residenceId);

  const rows: RoomRow[] = filteredRoomTypes.map(roomType => {
    const residenceInfo = residenceMap.get(roomType.residenceId);
    const residenceName = residenceInfo?.name ?? '—';
    const residenceAddress = residenceInfo?.address ?? '—';

    const matchingContracts = contracts.filter(c => c.roomTypeId === roomType.id);

    const contractBars: OccupancyBar[] = [];

    for (const contract of matchingContracts) {
      if (filters.contractStatus !== 'all' && contract.status !== filters.contractStatus) continue;

      const contractStart = contract.startDate.toDate();
      const plannedEnd = contract.endDate.toDate();
      // Terminated contracts stop at the earlier of terminatedAt / planned end.
      const effectiveEnd =
        contract.status === 'terminated' && contract.terminatedAt
          ? new Date(Math.min(contract.terminatedAt.toDate().getTime(), plannedEnd.getTime()))
          : plannedEnd;
      // endDate is inclusive: exclusive end is the day after.
      const geo = monthBarGeometry(contractStart, addDaysExclusive(effectiveEnd), year);
      if (!geo) continue;

      const studentInfo: StudentInfo = {
        contractId: contract.id,
        studentId: contract.studentId,
        studentName: contract.studentName,
        studentEmail: contract.studentEmail,
        contactPhone: contract.contactPhone,
        contactEmail: contract.contactEmail,
        monthlyValue: contract.monthlyValue,
        monthlyKwhLimit: contract.monthlyKwhLimit,
        startDate: contractStart,
        endDate: effectiveEnd,
        contractStatus: contract.status,
      };

      contractBars.push({
        barId: contract.id,
        source: 'contract',
        contractId: contract.id,
        studentName: contract.studentName,
        studentEmail: contract.studentEmail,
        contactPhone: contract.contactPhone,
        contactEmail: contract.contactEmail,
        roomTypeName: contract.roomTypeName,
        residenceName: contract.residenceName,
        residenceId: contract.residenceId,
        monthlyValue: contract.monthlyValue,
        monthlyKwhLimit: contract.monthlyKwhLimit,
        startDate: contractStart,
        endDate: effectiveEnd,
        contractStatus: contract.status,
        ...geo,
        students: [studentInfo],
      });
    }

    contractBars.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const mergedBars = mergeOverlappingBars(contractBars, year);

    // Booking entries: admin blocks, offline bookings, and guest bookings
    // (approved + pending). Each is its own bar, never merged with contracts.
    // A booking's checkOut is the exclusive end.
    const bookingBars: OccupancyBar[] = [];
    for (const b of calendarBookings) {
      if (b.roomTypeId !== roomType.id) continue;

      const bStart = b.checkIn.toDate();
      const bEnd = b.checkOut.toDate();
      const isAdmin = b.type === 'admin';

      let source: OccupancyBar['source'];
      if (b.status === 'blocked') source = 'admin-block';
      else if (b.status === 'pending') source = 'pending-booking';
      else source = isAdmin ? 'admin-booking' : 'guest-booking';

      // Once an approved guest booking has a contract covering it, the
      // contract bar is the authoritative record — skip the booking bar so
      // the same stay doesn't render twice.
      if (source === 'guest-booking') {
        const covered = matchingContracts.some(c => {
          const cEnd =
            c.status === 'terminated' && c.terminatedAt ? c.terminatedAt.toDate() : c.endDate.toDate();
          return bStart < cEnd && bEnd > c.startDate.toDate();
        });
        if (covered) continue;
      }

      const geo = monthBarGeometry(bStart, bEnd, year);
      if (!geo) continue;

      // Real arrival/departure (exact days the student picked) — rendered as
      // a darker inner strip inside the month-granular billing bar.
      const arrival = b.arrivalDate?.toDate();
      const departure = b.departureDate?.toDate();
      let actual: Pick<OccupancyBar, 'actualStart' | 'actualEnd' | 'actualLeftPx' | 'actualWidthPx'> = {};
      if (arrival && departure && arrival.getFullYear() <= year && departure.getFullYear() >= year) {
        const left = dayOffsetPx(arrival, year);
        const width = Math.max(2, dayOffsetPx(departure, year, true) - left);
        actual = { actualStart: arrival, actualEnd: departure, actualLeftPx: left, actualWidthPx: width };
      }

      bookingBars.push({
        barId: b.id,
        source,
        studentName:
          source === 'admin-block'
            ? (b.adminNote?.trim() || 'Admin block')
            : (b.guests[0]?.name || (isAdmin ? 'Offline booking' : 'Booking')),
        roomTypeName: roomType.name,
        residenceName,
        residenceId: roomType.residenceId,
        startDate: bStart,
        endDate: bEnd,
        adminNote: b.adminNote,
        ...actual,
        ...geo,
      });
    }

    const bars = [...mergedBars, ...bookingBars];

    // Assign lanes so overlapping bars stack vertically instead of hiding
    // each other — common now that pendings don't block availability, so
    // several pending bookings can cover the same period on the same unit.
    bars.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const laneEnds: number[] = [];
    for (const bar of bars) {
      let lane = laneEnds.findIndex(end => end <= bar.startDate.getTime());
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      bar.lane = lane;
      laneEnds[lane] = bar.endDate.getTime();
    }

    return {
      key: roomType.id,
      roomTypeId: roomType.id,
      roomTypeName: roomType.name,
      residenceId: roomType.residenceId,
      residenceName,
      residenceAddress,
      priceByOccupancy: roomType.priceByOccupancy ?? [],
      bars,
      laneCount: Math.max(1, laneEnds.length),
      // Pending bookings don't block availability, so they don't count as occupancy.
      isOccupiedInMonth: bars.some(bar => bar.source !== 'pending-booking'),
      typologyKey: roomType.typologyKey,
      createdAtMillis: roomType.createdAt?.toMillis?.() ?? 0,
    };
  });

  // Apply status filter (reused from month view semantics).
  let filtered = rows;
  if (filters.statusView === 'occupied') {
    filtered = rows.filter(r => r.isOccupiedInMonth);
  } else if (filters.statusView === 'available') {
    filtered = rows.filter(r => !r.isOccupiedInMonth);
  }

  // Sort: residence name then room type name.
  filtered.sort((a, b) => {
    const res = a.residenceName.localeCompare(b.residenceName);
    if (res !== 0) return res;
    return a.roomTypeName.localeCompare(b.roomTypeName);
  });

  return filtered;
}

/**
 * Year-view data hook. Fetches the same four unbounded sources as the month
 * view and returns one RoomRow per RoomType with bars positioned in the
 * 12-month-column grid for `year`.
 */
export const useYearCalendarData = (year: number, filters: AvailabilityFilters) => {
  const { data: roomTypes = [], isLoading: roomTypesLoading } = useAdminRoomTypes();
  const { data: residences = [], isLoading: residencesLoading } = useAdminResidences();
  const { data: bookings = [], isLoading: bookingsLoading } = useAdminBookings();
  const { data: contracts = [], isLoading: contractsLoading } = useAdminContracts(
    useMemo(
      () => ({
        residenceId: filters.residenceId !== 'all' ? filters.residenceId : undefined,
      }),
      [filters.residenceId]
    )
  );

  // Everything that should appear on the calendar: admin blocks, offline
  // bookings, approved guest bookings (occupying), and pending guest bookings
  // (demand — rendered but not counted as occupancy).
  const calendarBookings = useMemo(
    () =>
      bookings.filter(
        b =>
          b.status === 'blocked' ||
          b.status === 'approved' ||
          (b.status === 'pending' && b.type !== 'admin')
      ),
    [bookings]
  );

  const residenceMap = useMemo(() => {
    const map = new Map<string, { name: string; address: string }>();
    residences.forEach((r: Residence) => map.set(r.id, { name: r.name, address: r.address }));
    return map;
  }, [residences]);

  const rows = useMemo(
    () => buildRoomRows(roomTypes, contracts, calendarBookings, residenceMap, year, filters),
    [roomTypes, contracts, calendarBookings, residenceMap, year, filters]
  );

  const isLoading = roomTypesLoading || residencesLoading || contractsLoading || bookingsLoading;

  return { rows, isLoading, year };
};
